import React, { useState, useEffect } from 'react';
import { useBranding } from '../../context/branding/branding.context';
import { useColorMode } from '../../context/colorMode/colorMode.context';
import DefaultLogo from '../../assets/images/PoweredByDQ_Default.png';
import WhiteLogo from '../../assets/images/PoweredByDQ_white.png';
import { toBrandingLogoSrc } from '../../utils/toBrandingLogoSrc';

/**
 * Workspace logo.
 *
 * Renders the workspace's custom logo when set, otherwise the bundled DQ logo.
 * The logo is NEVER stretched: height is fixed and width is auto with
 * `object-fit: contain`, so any aspect ratio is scaled to fit cleanly.
 *
 * Props:
 *   height    {number}  rendered height in px (default 60)
 *   className {string}  optional class (kept for callers using makeStyles)
 *   style     {object}  optional style overrides
 *   onClick   {func}    optional click handler
 *   alt       {string}  alt text
 */
const BrandingLogo = ({ height = 60, className, style, onClick, alt = '' }) => {
    const { branding } = useBranding();
    const { isDark } = useColorMode();
    const customUrl = branding && branding.logo_url;
    const [imgFailed, setImgFailed] = useState(false);

    useEffect(() => { setImgFailed(false); }, [customUrl]);

    const fallbackLogo = isDark ? WhiteLogo : DefaultLogo;
    const src = (!imgFailed && customUrl) ? toBrandingLogoSrc(customUrl) : fallbackLogo;

    return (
        <img
            key={customUrl || 'default'}
            src={src}
            alt={alt}
            role="presentation"
            className={className}
            onClick={onClick}
            onError={() => setImgFailed(true)}
            style={{
                height,
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
                display: 'block',
                cursor: onClick ? 'pointer' : 'default',
                ...style,
            }}
        />
    );
};

export default BrandingLogo;
